import { clearSession, signIn as signInEntity } from 'actionCreators';
import {
  signIn as authSignin,
  getAuthToken,
  getMeGalaxy,
  getMeRails,
  getTempToken,
} from 'services';
import { accessTokenManager } from 'core/accessTokenManager';
import { logoutApi } from 'AuthModule/services/logoutApi';
import { redirectToLoginScreen } from 'utils';

import {
  ExchangeTokenSuccessResponse,
  FetchTempTokenParams,
  FetchTempTokenSuccessResponse,
  ResetPasswordType,
  SignInType,
  SignUpType,
} from 'types';

import { AUTH_ERRORS, SIGN_OUT_REQUEST_TIMEOUT_MS } from '@constants';

import type { AppDispatchType } from 'store';

import { callApi } from 'thunk';
import { createAsyncThunk } from '@reduxjs/toolkit';
import ApiClientManager from 'core/apiClient';
import { Tenant } from 'TenantModule/types';

import { SignInResponse, GetMeGalaxyResponse } from 'AuthModule/services';
import { AxiosResponse } from 'axios';
import { ApiError } from 'services/api';
import { User } from 'AuthModule/reducers';

/**
 * Deprecated. We are using auth token to sign in from now
 */
export const signIn = (credentials: SignInType) => async (dispatch: AppDispatchType) => {
  const api = () => authSignin(credentials);

  const actionCreators = signInEntity();

  return callApi<SignInType, AxiosResponse<SignInResponse>, ApiError>(
    api,
    {
      request: actionCreators.request as any,
      success: actionCreators.success as any,
      failure: actionCreators.failure as any,
      requestPayload: { ...credentials },
      dispatch,
    },
    {
      enhanceErrorFn: (e: ApiError) => {
        return {
          ...e,
          message:
            'Sign in failed. You either are not a super admin, or entered incorrect credentials',
        };
      },
      errorType: AUTH_ERRORS.AUTHENTICATION_ERROR,
    },
  );
};

/**
 * Extended User type with tenant information
 */
export type FetchMeResponse = User & {
  tenantName: string;
  tenantInfo: {
    id: string;
    name: string;
    [key: string]: unknown;
  };
};

/**
 * This must only be called once when user is NOT AUTHENTICATED
 */
export const fetchMe = createAsyncThunk<
  User,
  {
    meta?: IMeta<{
      tenantName: string;
      tenantInfo: Nullable<Tenant>;
    }> & {
      realmId?: string;
      tenantName?: string;
      providedAuthToken: string;
    };
  }
>('fetchMe', async ({ meta }) => {
  const { onSuccess, onFailure, realmId, tenantName, providedAuthToken } = meta || {};

  const token = providedAuthToken!;

  try {
    const railsResponse = await getMeRails(
      token,
      ApiClientManager.buildRailsServerApiConfigs({
        realmId,
        tenantName,
      }),
    );

    // to get tenant information for the tenant to log in
    const glxResponse = await getMeGalaxy(
      token,
      ApiClientManager.buildGalaxyServerApiConfigs({
        realmId,
        tenantName,
      }),
    );

    onSuccess?.({
      response: {
        tenantName: glxResponse.data.tenant,
        tenantInfo: glxResponse.data.tenantInfo as unknown as Nullable<Tenant>,
      },
    });
    return railsResponse.data;
  } catch (e) {
    onFailure?.({ error: e });
    throw e;
  }
});

export const exchangeToken = createAsyncThunk<
  ExchangeTokenSuccessResponse,
  {
    tempToken: string;
    // this is only used in auth layer
    meta?: IMeta<ExchangeTokenSuccessResponse> & {
      realmId?: string;
      tenantName?: string;
    };
  }
>('exchangeToken', async ({ tempToken, meta }, { dispatch, getState, rejectWithValue }) => {
  const { onSuccess, onFailure, realmId, tenantName } = meta || {};
  try {
    // Header building for fetching auth token
    const response = await getAuthToken(
      tempToken,
      ApiClientManager.buildRailsServerApiConfigs({
        tenantName,
        realmId,
      }),
    );
    if (response.data?.auth_token) {
      accessTokenManager.setAccessToken(response.data.auth_token);
    }
    onSuccess?.({ response: response.data });
    return response.data;
  } catch (e) {
    // Surface exchange failures (expired/invalid temp token) to the auth
    // layer so it can fail over instead of loading forever (GLX-1743)
    onFailure?.({ error: e });
    throw e;
  }
});

export const fetchTempToken = createAsyncThunk<FetchTempTokenSuccessResponse, FetchTempTokenParams>(
  'fetchTempToken',
  async (params, { dispatch, getState, rejectWithValue }) => {
    try {
      const { onFailure, onSuccess, auth_token, tenantName, realmId } = params;
      const response = await getTempToken(
        auth_token,
        ApiClientManager.buildRailsServerApiConfigs({ tenantName, realmId }),
      );
      onSuccess?.({ response });
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const revokeSession = async (token: string) => {
  try {
    await logoutApi({
      params: undefined,
      token,
      config: { timeout: SIGN_OUT_REQUEST_TIMEOUT_MS },
    });
  } catch {
    // The session ends locally whether or not the server heard about it.
  }
};

export const signOut = createAsyncThunk<void, void>('signOut', async (_, { dispatch }) => {
  // galaxy-server only revokes for a verified bearer; with none, skip it.
  const token = accessTokenManager.getAccessToken();
  // Sent first: clearSession resets the API client config the request reads.
  const revoke = token ? revokeSession(token) : undefined;
  dispatch(clearSession());
  await revoke;
  // An in-flight response can store a refreshed token during the wait.
  accessTokenManager.clearAccessToken();

  // Need to clear out state and let the revoke finish before redirecting
  redirectToLoginScreen();
});
